/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { useRef, useState, useEffect } from "react";
import { renderList } from "common/vue/render-list";

export default function AreasOfInterestOverlay({
  areasOfInterest,
  naturalWidth,
  naturalHeight,
  isVisible = true,
  pinId,
  segmentationMaskUrl = undefined,
  // @ts-expect-error TS(6133): 'restProps' is declared but its value is never rea... Remove this comment to see the full error message
  ...restProps
}: any) {
  const WHITE_THRESHOLD = 200;

  // --- Type guards ---
  function isBoundingBox2D(obj: any) {
    return obj != null && "bbox2d" in obj && Array.isArray(obj.bbox2d);
  }

  function isMaskObject(obj: any) {
    return obj != null && "maskUrl" in obj;
  }

  // --- Computed properties ---
  function isBoundingBox() {
    return areasOfInterest?.type === "bounding_box";
  }

  function isMask() {
    return areasOfInterest?.type === "mask";
  }

  function svgMaskId() {
    return `aoi-mask-${pinId}`;
  }

  function transformedBoxes() {
    if (!isBoundingBox()) return [];
    if (!naturalWidth || !naturalHeight) return [];
    return areasOfInterest.objects.filter(isBoundingBox2D).map((obj: any) => {
      const [x1, y1, x2, y2] = obj.bbox2d;
      return {
        x: (x1 / naturalWidth) * 100,
        y: (y1 / naturalHeight) * 100,
        width: ((x2 - x1) / naturalWidth) * 100,
        height: ((y2 - y1) / naturalHeight) * 100
      };
    });
  }

  function effectiveMaskUrl() {
    if (segmentationMaskUrl) return segmentationMaskUrl;
    const obj = areasOfInterest?.objects?.find(isMaskObject);
    return obj?.maskUrl ?? null;
  }

  // --- Mask overlay state and refs ---
  const canvasRef = useRef(null);
  const [maskImageLoaded, setMaskImageLoaded] = useState(null);
  const maskResizeObserverRef = useRef(null);

  // --- Pixel filter ---
  function applyMaskTransparencyFilter(imageData: any) {
    const { data } = imageData;
    const len = data.length;
    for (let i = 0; i < len; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      if (r >= WHITE_THRESHOLD && g >= WHITE_THRESHOLD && b >= WHITE_THRESHOLD) {
        // White pixels (area of interest) -> transparent
        data[i + 3] = 0;
      } else {
        // Non-white pixels -> 70% opacity overlay
        data[i + 3] = 178;
      }
    }
  }

  function drawMask() {
    const canvas = canvasRef.current;
    const img = maskImageLoaded;
    if (!canvas || !img) return;
    // @ts-expect-error TS(2339): Property 'clientWidth' does not exist on type 'nev... Remove this comment to see the full error message
    const displayWidth = Math.round(canvas.clientWidth);
    // @ts-expect-error TS(2339): Property 'clientHeight' does not exist on type 'ne... Remove this comment to see the full error message
    const displayHeight = Math.round(canvas.clientHeight);
    if (!displayWidth || !displayHeight) return;
    // @ts-expect-error TS(2339): Property 'width' does not exist on type 'never'.
    canvas.width = displayWidth;
    // @ts-expect-error TS(2339): Property 'height' does not exist on type 'never'.
    canvas.height = displayHeight;
    // @ts-expect-error TS(2339): Property 'getContext' does not exist on type 'neve... Remove this comment to see the full error message
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, displayWidth, displayHeight);
    try {
      const imageData = ctx.getImageData(0, 0, displayWidth, displayHeight);
      applyMaskTransparencyFilter(imageData);
      ctx.putImageData(imageData, 0, 0);
    } catch {
      // CORS / SecurityError — canvas tainted, pixel filter cannot be applied
    }
  }

  function loadMaskImage(url: any) {
    if (!url) {
      setMaskImageLoaded(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // @ts-expect-error TS(2345): Argument of type 'HTMLImageElement' is not assigna... Remove this comment to see the full error message
      setMaskImageLoaded(img);
    };
    img.onerror = () => {
      setMaskImageLoaded(null);
    };
    img.src = url;
  }

  // --- Load mask image when URL changes ---
  useEffect(() => {
    loadMaskImage(effectiveMaskUrl());
  }, [segmentationMaskUrl, areasOfInterest]);

  // --- Set up ResizeObserver when canvas mounts ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (maskResizeObserverRef.current) {
      // @ts-expect-error TS(2339): Property 'disconnect' does not exist on type 'neve... Remove this comment to see the full error message
      maskResizeObserverRef.current.disconnect();
      maskResizeObserverRef.current = null;
    }

    // @ts-expect-error TS(2322): Type 'ResizeObserver' is not assignable to type 'n... Remove this comment to see the full error message
    maskResizeObserverRef.current = new ResizeObserver(() => {
      if (maskImageLoaded) drawMask();
    });
    // @ts-expect-error TS(2531): Object is possibly 'null'.
    maskResizeObserverRef.current.observe(canvas);
    if (maskImageLoaded) drawMask();

    // eslint-disable-next-line consistent-return
    return () => {
      if (maskResizeObserverRef.current) {
        // @ts-expect-error TS(2339): Property 'disconnect' does not exist on type 'neve... Remove this comment to see the full error message
        maskResizeObserverRef.current.disconnect();
        maskResizeObserverRef.current = null;
      }
    };
  }, [maskImageLoaded]);

  return (
    <>
      {isVisible && isBoundingBox() && transformedBoxes().length > 0 ? (
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          data-testid="areas-of-interest-overlay"
          preserveAspectRatio="none"
        >
          <defs>
            <mask id={svgMaskId()}>
              <rect width="100%" height="100%" fill="white" />
              {renderList(transformedBoxes(), (box, idx) => (
                <rect
                  key={idx}
                  x={`${box.x}%`}
                  y={`${box.y}%`}
                  width={`${box.width}%`}
                  height={`${box.height}%`}
                  fill="black"
                />
              ))}
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.7)"
            mask={`url(#${svgMaskId()})`}
          />
        </svg>
      ) : null}
      {isVisible && isMask() && effectiveMaskUrl() ? (
        <canvas
          className="pointer-events-none absolute inset-0 h-full w-full"
          ref={canvasRef}
          data-testid="areas-of-interest-mask-overlay"
        />
      ) : null}
    </>
  );
}

AreasOfInterestOverlay.displayName = "AreasOfInterestOverlay";
